/**
 * „Wo ist …?" -- der Startbildschirm (KONZEPT.md §10, §20).
 *
 * Die mit Abstand häufigste Frage bekommt die größte Fläche. Kein Dashboard,
 * keine Kacheln: ein Suchfeld, und darunter die Antwort.
 */

import { useDeferredValue, useMemo } from "react";
import { Link } from "react-router";
import { useBestand } from "../lokal/bestand.js";
import { packstueckStandort } from "../lokal/packstueck.js";
import { suchen } from "../lokal/suche.js";
import { Leer, Nummer, StatusPunkt, VerbleibSchild } from "../ui/bausteine.jsx";
import { useAdressText } from "../ui/navigation.jsx";

/** Mehr liest an der Einsatzstelle niemand durch. */
const HOECHSTENS = 30;

export function WoIst() {
  // In der Adresse: Nach dem Zurück sind Suchbegriff und Treffer wieder da (1.1).
  const [suche, setSuche] = useAdressText("suche");
  const gekuerzt = suche.trim();

  const bestand = useBestand();
  // Beim schnellen Tippen darf das Feld nicht hinter der Liste herhinken.
  const gesucht = useDeferredValue(gekuerzt);

  // Aus der lokalen Kopie, bei jedem Tastendruck: offline genauso wie
  // online (KONZEPT.md §15.1).
  const data = useMemo(() => {
    // Erst ab zwei Zeichen suchen -- ein einzelner Buchstabe liefert den
    // halben Bestand und hilft niemandem.
    if (bestand === undefined || gesucht.length < 2) return undefined;
    const { treffer, gesamt } = suchen(bestand.artikel.values(), bestand.zustaende, gesucht, {
      limit: HOECHSTENS,
    });
    return {
      gesamt,
      treffer: treffer.map(({ artikel, zustand }) => {
        const ort =
          zustand.lagerort_id === null ? undefined : bestand.lagerorte.get(zustand.lagerort_id);
        return {
          artikel_id: artikel.id,
          inventarnummer: artikel.inventarnummer,
          bezeichnung: artikel.bezeichnung,
          verbleib: zustand.verbleib,
          status: zustand.status,
          bestand: zustand.bestand,
          // Im Packstueck: auch sagen, wo das Packstueck gerade ist (ADR 0012).
          lagerort:
            ort === undefined
              ? null
              : [`${ort.kennung} — ${ort.bezeichnung}`, packstueckStandort(bestand, ort.id)]
                  .filter((t) => t !== null)
                  .join(", "),
        };
      }),
    };
  }, [bestand, gesucht]);

  return (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-2">
        <span className="text-lg font-bold">Wo ist …?</span>
        <input
          className="min-h-14 rounded-xl border-2 border-neutral-300 bg-white px-4 text-lg dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value);
          }}
          placeholder="Leitungsroller, 0141-900639, THW-81234 …"
          autoComplete="off"
          // Auf dem Handy soll die Tastatur sofort da sein: Wer diese Seite
          // oeffnet, will suchen.
          autoFocus
        />
      </label>

      {gekuerzt.length < 2 && (
        <Leer>
          Tippen oder scannen. Es geht die Bezeichnung, die Inventarnummer — auch ohne Bindestrich —
          und das Kennzeichen eines Fahrzeugs.
        </Leer>
      )}

      {gekuerzt.length >= 2 && data !== undefined && data.treffer.length === 0 && (
        <Leer>
          Nichts gefunden. Vielleicht ist es noch nicht erfasst — dann hilft ein Scan des Etiketts.
        </Leer>
      )}

      {data !== undefined && data.treffer.length > 0 && (
        <>
          <p className="text-sm text-neutral-500">
            {data.gesamt === data.treffer.length
              ? `${String(data.gesamt)} gefunden`
              : `${String(data.treffer.length)} von ${String(data.gesamt)} gezeigt`}
          </p>
          <ul className="flex flex-col gap-2">
            {data.treffer.map((t) => (
              <li key={t.artikel_id}>
                <Link
                  to={`/artikel/${t.artikel_id}`}
                  className="flex min-h-16 flex-col justify-center gap-1 rounded-xl bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800 dark:active:bg-neutral-700"
                >
                  <span className="font-semibold">{t.bezeichnung}</span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Nummer>{t.inventarnummer}</Nummer>
                    <StatusPunkt status={t.status} />
                    {t.verbleib !== "im_lager" && <VerbleibSchild verbleib={t.verbleib} />}
                    {t.bestand !== null && (
                      <span className="text-sm text-neutral-600 dark:text-neutral-300">
                        Bestand {t.bestand}
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-300">
                    {t.lagerort ?? "Ort unbekannt"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
